import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Ear, Headphones, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import Seo, { orgSchema } from "@/components/Seo";
import { toast } from "sonner";

const SITE = "https://signiastylettoix.com";
const FREQS = [500, 1000, 2000, 4000];
const LEVELS = [
    { db: 25, gain: 0.02 },
    { db: 40, gain: 0.08 },
    { db: 55, gain: 0.28 },
    { db: 70, gain: 0.85 },
];

export default function HearingTest() {
    const [stage, setStage] = useState("intro"); // intro | test | contact | results
    const [ear, setEar] = useState("right");
    const [fi, setFi] = useState(0);
    const [li, setLi] = useState(0);
    const [answers, setAnswers] = useState({ left: [], right: [] });
    const [playing, setPlaying] = useState(false);
    const [result, setResult] = useState(null);
    const [contact, setContact] = useState({ name: "", phone: "" });
    const [busy, setBusy] = useState(false);
    const ctxRef = useRef(null);

    const playTone = () => {
        if (playing) return;
        const ctx = ctxRef.current || new (window.AudioContext || window.webkitAudioContext)();
        ctxRef.current = ctx;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const pan = ctx.createStereoPanner();
        osc.frequency.value = FREQS[fi];
        gain.gain.value = 0;
        pan.pan.value = ear === "left" ? -1 : 1;
        osc.connect(gain); gain.connect(pan); pan.connect(ctx.destination);
        const t = ctx.currentTime;
        gain.gain.linearRampToValueAtTime(LEVELS[li].gain, t + 0.05);
        gain.gain.setValueAtTime(LEVELS[li].gain, t + 0.9);
        gain.gain.linearRampToValueAtTime(0, t + 1.0);
        osc.start(t); osc.stop(t + 1.05);
        setPlaying(true);
        setTimeout(() => setPlaying(false), 1100);
    };

    const advance = (heardDb) => {
        const rec = { freq: FREQS[fi], heard_at_db: heardDb };
        const next = { ...answers, [ear]: [...answers[ear], rec] };
        setAnswers(next);
        setLi(0);
        if (fi < FREQS.length - 1) { setFi(fi + 1); return; }
        if (ear === "right") { setEar("left"); setFi(0); return; }
        setStage("contact");
    };

    const onHeard = () => advance(LEVELS[li].db);
    const onNotHeard = () => {
        if (li < LEVELS.length - 1) setLi(li + 1);
        else advance(null);
    };

    const submit = async (skip = false) => {
        setBusy(true);
        try {
            const { data } = await api.post("/hearing-test/submit", {
                left: answers.left, right: answers.right,
                name: skip ? null : contact.name || null,
                phone: skip ? null : contact.phone || null,
            });
            setResult(data);
            setStage("results");
            if (!skip && contact.phone) toast.success("Report saved — our audiologist will call to schedule your free clinical test");
        } catch { toast.error("Could not submit — please try again"); } finally { setBusy(false); }
    };

    const progress = (ear === "right" ? fi : FREQS.length + fi) / (FREQS.length * 2) * 100;

    return (
        <>
            <Seo title="Free Online Hearing Test — 3-Minute Screening | SigniaStylettoIX"
                description="Take a free 3-minute online hearing screening: tone-based, per ear, with plain-language results and next steps. Not a diagnosis — but the right first step."
                canonical={`${SITE}/hearing-test`} schema={{ "@context": "https://schema.org", "@graph": [orgSchema()] }} />
            <section className="pt-12 md:pt-16 pb-16">
                <div className="container-x max-w-[760px]">
                    <p className="eyebrow inline-flex items-center gap-2"><Ear size={12} /> Free Online Screening</p>

                    {stage === "intro" && (
                        <div data-testid="ht-intro">
                            <h1 className="font-serif text-[36px] md:text-[50px] leading-[1.08] mt-4">Test Your Hearing in 3 Minutes</h1>
                            <p className="mt-5 text-[16.5px] text-[#4a5568]">A tone-based screening, one ear at a time. It indicates whether a full clinical test is worth booking — it is <strong>not a diagnosis</strong>.</p>
                            <div className="mt-6 p-5 bg-white border border-[#e2ddd4] rounded-2xl space-y-3 text-[15px]">
                                <p className="flex items-start gap-2"><Headphones size={18} className="text-[#003E4D] mt-0.5 shrink-0" /> <span><strong>Use headphones or earphones</strong> — speaker results are unreliable.</span></p>
                                <p className="flex items-start gap-2"><CheckCircle2 size={18} className="text-[#003E4D] mt-0.5 shrink-0" /> <span>Sit in a <strong>quiet room</strong> and set your device volume to about 50%.</span></p>
                                <p className="flex items-start gap-2"><CheckCircle2 size={18} className="text-[#003E4D] mt-0.5 shrink-0" /> <span>You'll hear short beeps at different pitches — tap whether you heard each one.</span></p>
                            </div>
                            <button onClick={() => setStage("test")} className="btn-primary mt-8" data-testid="ht-start">Start the Test <ArrowRight size={16} /></button>
                        </div>
                    )}

                    {stage === "test" && (
                        <div data-testid="ht-test">
                            <h1 className="font-serif text-[32px] md:text-[42px] mt-4 capitalize">{ear} ear — tone {fi + 1} of {FREQS.length}</h1>
                            <div className="mt-4 h-2 bg-[#e2ddd4] rounded-full overflow-hidden"><div className="h-full bg-[#003E4D] transition-all" style={{ width: `${progress}%` }} /></div>
                            <p className="mt-6 text-[15.5px] text-[#4a5568]">Press play, listen carefully in your <strong>{ear}</strong> ear, then answer honestly. Each tone lasts one second.</p>
                            <div className="mt-8 flex flex-col items-center gap-6 p-8 bg-white border border-[#e2ddd4] rounded-2xl">
                                <button onClick={playTone} disabled={playing} data-testid="ht-play"
                                    className={`w-28 h-28 rounded-full text-white text-[15px] font-medium transition ${playing ? "bg-[#0F6B7D] animate-pulse" : "bg-[#003E4D] hover:scale-105"}`}>
                                    {playing ? "Playing…" : "Play Tone"}
                                </button>
                                <div className="flex gap-3">
                                    <button onClick={onHeard} className="btn-primary !bg-[#1a5c3a]" data-testid="ht-heard"><CheckCircle2 size={16} /> I heard it</button>
                                    <button onClick={onNotHeard} className="btn-secondary" data-testid="ht-not-heard"><XCircle size={16} /> I didn't hear it</button>
                                </div>
                                <p className="text-[12px] text-[#9CA3AF]">If you didn't hear it, we'll replay the same pitch a little louder.</p>
                            </div>
                        </div>
                    )}

                    {stage === "contact" && (
                        <div data-testid="ht-contact">
                            <h1 className="font-serif text-[32px] md:text-[42px] mt-4">Your results are ready</h1>
                            <p className="mt-4 text-[15.5px] text-[#4a5568]">Add your details to save the report and get a <strong>free clinical audiogram</strong> scheduled (home visit available) — or view results without saving.</p>
                            <div className="mt-6 p-6 bg-white border border-[#e2ddd4] rounded-2xl space-y-4 max-w-[480px]">
                                <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Your name" data-testid="ht-name" className="w-full border border-[#e2ddd4] rounded-xl px-4 py-3 text-[15px]" />
                                <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Mobile number" data-testid="ht-phone" className="w-full border border-[#e2ddd4] rounded-xl px-4 py-3 text-[15px]" />
                                <div className="flex gap-3">
                                    <button onClick={() => submit(false)} disabled={busy || !contact.phone || !contact.name} className="btn-primary" data-testid="ht-submit">See My Results</button>
                                    <button onClick={() => submit(true)} disabled={busy} className="btn-secondary" data-testid="ht-skip">Skip & view results</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {stage === "results" && result && (
                        <div data-testid="ht-results">
                            <h1 className="font-serif text-[32px] md:text-[44px] mt-4">Your screening result</h1>
                            <div className={`mt-6 p-6 rounded-2xl border text-[16px] ${result.overall_band === "normal" ? "bg-[#f0f7f4] border-[#cfe3d8]" : "bg-[#fff8e6] border-[#f5d27a]/50"}`} data-testid="ht-band">
                                <p className="font-medium text-[18px]">{result.overall_band === "normal" ? "In the normal screening range" : `Signs consistent with ${result.overall_band} hearing difficulty`}</p>
                                <p className="mt-2 text-[15px] text-[#4a5568]">{result.advice}</p>
                            </div>
                            <div className="mt-6 grid sm:grid-cols-2 gap-4">
                                {["right", "left"].map((e) => (
                                    <div key={e} className="p-5 bg-white border border-[#e2ddd4] rounded-2xl">
                                        <p className="font-medium capitalize">{e} ear — {result[e].label}</p>
                                        <div className="mt-3 space-y-2">
                                            {answers[e].map((r, i) => (
                                                <div key={i} className="flex items-center gap-3 text-[13.5px]">
                                                    <span className="w-16 text-[#4a5568]">{r.freq} Hz</span>
                                                    <div className="flex-1 h-2 bg-[#efece6] rounded-full overflow-hidden">
                                                        <div className={`h-full ${r.heard_at_db === null ? "bg-[#c0392b]" : r.heard_at_db <= 25 ? "bg-[#1a5c3a]" : r.heard_at_db <= 40 ? "bg-[#B8860B]" : "bg-[#c0392b]"}`}
                                                            style={{ width: `${((r.heard_at_db ?? 85) / 85) * 100}%` }} />
                                                    </div>
                                                    <span className="w-20 text-right text-[#4a5568]">{r.heard_at_db === null ? "Not heard" : `~${r.heard_at_db} dB`}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-4 text-[12.5px] text-[#9CA3AF]">{result.disclaimer}</p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link to="/book-trial" className="btn-primary" data-testid="ht-book-clinical">Book Free Clinical Hearing Test <ArrowRight size={16} /></Link>
                                <Link to="/find" className="btn-secondary">Find My Hearing Aid Match</Link>
                                <Link to="/learn/hearing-test-what-to-expect" className="btn-secondary">What a clinical test involves</Link>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
